import { Injectable } from "@angular/core";
import { NotificationFeedItem } from "../models/notification-feed-item.model";

/**
 * "New Standard Published"/"Standard Modified"/"Exception Expiring" feed items are
 * computed on read, not stored rows, so the backend has nothing to persist a
 * read/unread flag against (NotificationFeedItem.notificationId is null for these).
 * This tracks "seen" for them client-side in localStorage instead, so they stop
 * showing as unread once viewed, per-browser.
 */
@Injectable({
  providedIn: "root",
})
export class ComputedNotificationSeenService {
  private readonly storageKey = "computedNotificationsSeen";

  private readSeenKeys(): Set<string> {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? new Set<string>(JSON.parse(raw)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  }

  private writeSeenKeys(keys: Set<string>): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(Array.from(keys)));
    } catch {
      // Best effort only - private browsing / blocked storage shouldn't break the page.
    }
  }

  private getKey(item: NotificationFeedItem): string | null {
    if (item.notificationId) {
      // Stored row - has its own real seen/unseen state, not handled here.
      return null;
    }
    if (
      item.category === "NewStandardPublished" ||
      item.category === "StandardModified"
    ) {
      return `standard-${item.standardId}-${item.category}`;
    }
    if (item.category === "ExceptionExpiring") {
      return `expiring-${item.addendumId}`;
    }
    return null;
  }

  isSeen(item: NotificationFeedItem): boolean {
    const key = this.getKey(item);
    if (!key) {
      return false;
    }
    return this.readSeenKeys().has(key);
  }

  markSeen(item: NotificationFeedItem): void {
    const key = this.getKey(item);
    if (!key) {
      return;
    }
    const keys = this.readSeenKeys();
    keys.add(key);
    this.writeSeenKeys(keys);
  }
}
